import React from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import Chart from "../../../parts/chart";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import getDayRangeString from "../../../utils/day-range-string";
import getSelectedMetrics from "../../../utils/get-selected-metrics";

import { PERIOD_FORMATS } from "../../../constants";
import { useRillContext } from "../../../rill/RillReact/share/base";

const PERIOD_OPTIONS = (t) => ({
  day: [{ text: t("coreAccessoryVolumeSplit.daily"), value: "day" }],
  week: [{ text: t("coreAccessoryVolumeSplit.weekly"), value: "week" }],
  month: [{ text: t("coreAccessoryVolumeSplit.monthly"), value: "month" }],
});

function View(props) {
  const { t } = useTranslation("dashboard");
  const { locals } = useRillContext();

  _.defaults(locals.query, {
    type: "total",
    units: "metric",
  });

  const { snapshot, query, momentTZ } = locals;
  const { units } = query;
  const user = _.get(snapshot, "user", {});
  const exercisesVolumeSplitCoreAccessory = _.get(
    snapshot,
    "data.exercisesVolumeSplitCoreAccessory",
    {}
  );
  const period = (query.period = _.get(snapshot, "data.period", "day"));
  const period_short = PERIOD_FORMATS[period];
  const startDate = momentTZ(_.get(snapshot, "data.start", momentTZ()));
  const endDate = momentTZ(_.get(snapshot, "data.end", momentTZ()));
  const selectedMetrics = getSelectedMetrics(
    exercisesVolumeSplitCoreAccessory,
    query.type
  );
  const keys = _(selectedMetrics)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();
  const labels = _.map(keys, (i) => `${period_short}${i + 1}`);
  const selectedMetricValues = _.values(selectedMetrics);

  const coreSeries = {
    name: "core",
    className: "core",
    data: _.map(keys, (key) => {
      return locals.fromMetric(
        _.sumBy(selectedMetricValues, `${key}.core`),
        units
      );
    }),
  };

  const accessorySeries = {
    name: "accessory",
    className: "accessory",
    data: _.map(keys, (key) => {
      return locals.fromMetric(
        _.sumBy(selectedMetricValues, `${key}.accessory`),
        units
      );
    }),
  };

  const totalSeries = _.zipWith(
    accessorySeries.data,
    coreSeries.data,
    (a = 0, b = 0) => a + b
  );
  const volumeSeries = _.concat(coreSeries, accessorySeries);
  const maxVolume = _.max(totalSeries);

  return (
    <div className="body metrics">
      <h1 className="alt">{t("coreAccessoryVolumeSplit.title")}</h1>
      <p>
        {t("coreAccessoryVolumeSplit.description")}
      </p>

      <div className="Chart-Frame bar-chart">
        <div className="head">
          <div className="right-side">
            <div className="group">
              <ToggleLinksInput
                name="period"
                options={PERIOD_OPTIONS(t)[period]}
              />
            </div>

            <div className="group">
              <ToggleLinksInput
                name="units"
                options={[
                  { text: t("coreAccessoryVolumeSplit.kg"), value: "metric" },
                  { text: t("coreAccessoryVolumeSplit.lbs"), value: "imperial" },
                ]}
              />
            </div>
          </div>

          <div className="left-side">
            <div className="group">
              <ToggleLinksInput
                className="tabs"
                name="type"
                options={[
                  { text: t("coreAccessoryVolumeSplit.total"), value: "total" },
                  { text: t("coreAccessoryVolumeSplit.squat"), value: "squat" },
                  { text: t("coreAccessoryVolumeSplit.bench"), value: "bench" },
                  { text: t("coreAccessoryVolumeSplit.deadlift"), value: "deadlift" },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="body">
          {!_.isEmpty(selectedMetrics) && (
            <div>
              <div className="legend">
                <div className="bar series core">{t("coreAccessoryVolumeSplit.primaryVolume")}</div>
                <div className="bar series accessory">{t("coreAccessoryVolumeSplit.secondaryVolume")}</div>
              </div>
              <div className="axis-labels">
                <div className="left">{t("coreAccessoryVolumeSplit.volume")}</div>
              </div>
              <div className="chart-fullsize">
                <div className="labels">
                  <Chart
                    type="Bar"
                    only="label"
                    data={{ series: volumeSeries }}
                    options={{
                      width: "60px",
                      height: "380px",
                      stackBars: true,
                      axisX: { showGrid: false, showLabel: false },
                      axisY: {
                        showGrid: false,
                        showLabel: true,
                        scaleMinSpace: 30,
                        onlyInteger: true,
                        high: maxVolume,
                        low: 0,
                      },
                    }}
                  />
                </div>

                <div className="data">
                  <Chart
                    type="Bar"
                    data={{
                      labels: labels,
                      series: volumeSeries,
                    }}
                    options={{
                      width: `${_.size(keys) * 80}px`,
                      height: "380px",
                      stackBars: true,
                      seriesBarDistance: 20,
                      chartPadding: { left: 35 },
                      axisX: { showGrid: false, showLabel: true },
                      axisY: {
                        showGrid: true,
                        showLabel: false,
                        scaleMinSpace: 30,
                        high: maxVolume,
                        low: 0,
                      },
                    }}
                    tooltip={(item, i) => {
                      const key = keys[i];
                      const date = startDate.clone().add(key, period);
                      const dayRange = getDayRangeString(
                        [date, date.clone().add(1, period).subtract(1, "day")],
                        query
                      );
                      const coreTotal =
                        _.round(_.get(coreSeries, ["data", i], 0), 1) || 0;
                      const accessoryTotal =
                        _.round(_.get(accessorySeries, ["data", i], 0), 1) || 0;
                      return (
                        <div className="tooltip" style={{ left: item.x }}>
                          <div className="details">
                            <div>
                              <b>{dayRange}</b>
                            </div>
                            <div>
                              {t("coreAccessoryVolumeSplit.core")}{" "}
                              <b>{locals.withWeight(coreTotal, units)}</b>
                            </div>
                            <div>
                              {t("coreAccessoryVolumeSplit.accessory")}{" "}
                              <b>{locals.withWeight(accessoryTotal, units)}</b>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default View;
